import React, { useCallback, useMemo } from 'react';
import {
  FieldType,
  IDataModelAddDerivedFieldInput,
} from '../../../../utils/apollo/graphqlSchema';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { useViewColumnsValidator } from '../validation';
import { formColumnsWidth } from '../../../pageForm/PageHeadersWidthConfig';
import GeneralFormItem from '../../../../components/GeneralFormItem';
import EditableTextArea from '../../../../components/EditableTextArea';
import { entityDescriptionSchema } from '../../../../components/EditableTextArea/validation';
import {
  ActionIcon,
  CenteredDiv,
  IconsRowDiv,
  NameWithIconWrapper,
  StyledFormUIRow,
} from '../DataModelerCommonUI';
import KubitTooltip from '../../../../components/KubitTooltip';
import { dataModelTooltipOptions } from '../../../../components/KubitTooltip/globalTooltips';
import TickCheckbox from '../../../../components/Checkbox';
import { SvgIcon } from '../../../../components/Icon';
import { colors } from '../../../../config/styles';
import {
  dataModelerRemoveDraftDerivedField,
  dataModelerSetUiDraftDerivedField,
  dataModelerUpdateDraftDerivedField,
} from '../../../../redux/slices/dataModeler';
import { addDerivedFieldInputToMeta } from './utils';
import DataModelerDerivedFieldDefinitionMeta from './DataModelerDerivedFieldDefinitionMeta';
import { fieldTypeName } from '../utils';
import DerivedDefinitionIcon from '../../../../components/DerivedDefinitionIcon';
import { dataModelerSelectCurrentUiState } from '../../../../redux/selectors/dataModeler';
import { typeParamsInputToTypeParamsFieldsFragment } from '../../../../redux/slices/dataModeler/utils';

interface IProps {
  field: IDataModelAddDerivedFieldInput;
}

const DataModelerDraftDerivedFieldRow = ({ field }: IProps) => {
  const dispatch = useAppDispatch();
  const { allOtherColumnsSet, allOtherFieldNamesSet, namesValidator } =
    useViewColumnsValidator({
      fieldKey: field.key,
    });

  const isDuplicateViewColumnError = allOtherColumnsSet.has(field.column);
  const isFieldNameError = allOtherFieldNamesSet.has(
    field.name ?? field.column,
  );
  const isFieldError = isDuplicateViewColumnError || isFieldNameError;

  const groupByDisabled = field.typeInput.type === FieldType.Array;
  const groupByTooltip = useMemo(
    () =>
      groupByDisabled
        ? dataModelTooltipOptions.groupbyCheckboxTooltipDisabledContent
        : dataModelTooltipOptions.groupbyCheckboxTooltipContent,
    [groupByDisabled],
  );

  const handleNameChange = useCallback(
    (newValue: string) => {
      dispatch(
        dataModelerUpdateDraftDerivedField({
          fieldKey: field.key,
          input: {
            name: newValue,
          },
        }),
      );
    },
    [dispatch, field.key],
  );

  const handleDescriptionChange = useCallback(
    (newValue: string) => {
      dispatch(
        dataModelerUpdateDraftDerivedField({
          fieldKey: field.key,
          input: {
            description: newValue,
          },
        }),
      );
    },
    [dispatch, field.key],
  );

  const handleFilterClick = useCallback(() => {
    dispatch(
      dataModelerUpdateDraftDerivedField({
        fieldKey: field.key,
        input: { filter: !field.filter },
      }),
    );
  }, [dispatch, field]);

  const handleGroupbyClick = useCallback(() => {
    dispatch(
      dataModelerUpdateDraftDerivedField({
        fieldKey: field.key,
        input: { groupby: !field.groupby },
      }),
    );
  }, [dispatch, field]);

  const handleRemoveClick = useCallback(() => {
    dispatch(
      dataModelerRemoveDraftDerivedField({
        fieldKey: field.key,
      }),
    );
  }, [dispatch, field.key]);

  const definitionMeta = useMemo(
    () => addDerivedFieldInputToMeta(field),
    [field],
  );

  const hasActiveEditedDerivedField = !!useAppSelector(
    dataModelerSelectCurrentUiState,
  )?.draftDerivedField;
  const canEditDefinition = !hasActiveEditedDerivedField;
  const handleEditDefinitionClick = useCallback(() => {
    if (!canEditDefinition) {
      return;
    }
    dispatch(
      dataModelerSetUiDraftDerivedField({
        draft: {
          fieldName: field.name ?? field.key,
          fieldType: field.typeInput.type,
          typeParams: typeParamsInputToTypeParamsFieldsFragment(
            field.column,
            field.typeInput.typeParams,
          ),
          rawSql: field.rawSql,
          fieldMeta: null,
          newFieldKey: field.key,
        },
      }),
    );
  }, [canEditDefinition, dispatch, field]);

  return (
    <StyledFormUIRow
      $isHighlighted={true}
      $gridTemplateColumns={formColumnsWidth.dataModelerFieldsEditor}
    >
      <GeneralFormItem
        key={'class'}
        label={field.typeInput.fieldClass}
        isDisabled={false}
        isBold={true}
      />
      <NameWithIconWrapper>
        <DerivedDefinitionIcon tooltipText={'Derived Field'} />
        <EditableTextArea
          isBold
          value={field.name ?? ''}
          disabled={false}
          placeholder={''}
          onChange={handleNameChange}
          defaultRows={2}
          validation={namesValidator}
        />
      </NameWithIconWrapper>
      <EditableTextArea
        isBold
        value={field.description ?? ''}
        disabled={false}
        placeholder={''}
        onChange={handleDescriptionChange}
        defaultRows={4}
        validation={entityDescriptionSchema}
      />
      <GeneralFormItem
        key={'viewColumn'}
        label={field.column}
        tooltipContent={field.column}
        isDisabled={false}
        isBold={true}
      />
      <DataModelerDerivedFieldDefinitionMeta
        definition={definitionMeta}
        isDisabled={false}
        isBold={true}
        canEdit={canEditDefinition}
        onEditClick={handleEditDefinitionClick}
        requireEditConfirmation={false}
      />
      <GeneralFormItem
        key={'type'}
        label={fieldTypeName(field.typeInput.type)}
        tooltipContent={fieldTypeName(field.typeInput.type)}
      />
      <CenteredDiv>
        <KubitTooltip
          {...dataModelTooltipOptions.filterCheckboxTooltipContent}
          noOpenDelay={true}
        >
          <TickCheckbox checked={field.filter} onChange={handleFilterClick} />
        </KubitTooltip>
      </CenteredDiv>
      <CenteredDiv>
        <KubitTooltip {...groupByTooltip} noOpenDelay={true}>
          <TickCheckbox
            checked={field.groupby}
            onChange={handleGroupbyClick}
            disabled={groupByDisabled}
          />
        </KubitTooltip>
      </CenteredDiv>
      <CenteredDiv>
        <KubitTooltip
          placement="top-start"
          content={
            isFieldError
              ? 'This field definition has errors. Click to remove'
              : 'Remove'
          }
          noOpenDelay={true}
        >
          <div onClick={handleRemoveClick}>
            {isFieldError && <ActionIcon name={'error-icon'} />}
            <ActionIcon name="icon-x" width={8} height={8} />
          </div>
        </KubitTooltip>
      </CenteredDiv>
      <KubitTooltip
        {...dataModelTooltipOptions.addedFieldIcon}
        noOpenDelay={true}
      >
        <IconsRowDiv>
          <SvgIcon
            name={'circle'}
            fill={colors.normalGreen}
            height={12}
            width={12}
          />
        </IconsRowDiv>
      </KubitTooltip>
    </StyledFormUIRow>
  );
};

export default DataModelerDraftDerivedFieldRow;
